'use client';

import { StudioPrimaryAction } from '@/components/visual-editor/chat/StudioComposerControls';
import { CreationGlassBackdrop } from '@/components/visual-editor/CreationGlassBackdrop';
import creationChrome from '@/components/visual-editor/CreationChrome.module.css';
import { useEffect, useRef, useState, type PointerEvent, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Pause, Play, RotateCcw, X, Volume2, VolumeX, GripHorizontal } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SystemLoadingIcon } from '@/components/ui/SystemLoading';
import { useDialogFocus } from '@/lib/hooks/useDialogFocus';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { useVideoElement } from '@/lib/hooks/useVideoElement';
import type { UserMaterial } from '@/lib/api/materials';
import { referenceVideoEditApi, type ReferenceSourceReceipt } from '@/lib/api/reference-video-edit';
import { getAssetStreamUrl } from '@/lib/api/media-proxy';
import { referenceVideoFrames } from '@/lib/reference-video-frames';
import { FULL_REFERENCE_CROP, MIN_REFERENCE_MS, clamp, resizeReferenceCrop, trimReferenceRange, referenceEditPayload, referenceTime, type ReferenceCrop } from '@/lib/reference-video-edit';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import styles from './KagemaneReferenceEditor.module.css';

export type ReferenceEditorSource = { file: File; material?: never } | { material: UserMaterial; file?: never };
const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type Gesture = { kind: 'crop' | 'timeline' | 'scrub'; handle: string; pointerId: number; left: number; x: number; y: number; width: number; height: number; crop: ReferenceCrop; range: [number, number] };

export function KagemaneReferenceEditor({ source, maxDurationMs = 30000, onClose, onConfirm }: {
  source: ReferenceEditorSource; maxDurationMs?: number; onClose: () => void; onConfirm: (material: UserMaterial) => void;
}) {
  const t = useTranslations('referenceVideoEditor');
  const { video, videoRef } = useVideoElement();
  const dialog = useRef<HTMLElement>(null), stage = useRef<HTMLDivElement>(null), frame = useRef<HTMLDivElement>(null), track = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null), busyRef = useRef(false), mounted = useRef(true);
  const pendingSeek = useRef<number | null>(null), seekFrame = useRef<number | null>(null);
  const editAbort = useRef<AbortController | null>(null);
  useEffect(() => () => {
    editAbort.current?.abort();
    if (seekFrame.current !== null) cancelAnimationFrame(seekFrame.current);
    seekFrame.current = null; pendingSeek.current = null; gesture.current = null;
  }, []);
  const uploaded = useRef<ReferenceSourceReceipt | null>(source.material ? { id: source.material.id } : null);
  const [url, setUrl] = useState('');
  const [metadata, setMetadata] = useState({ width: 0, height: 0, duration: 0 });
  const [size, setSize] = useState({ width: 680, height: 420 });
  const [crop, setCrop] = useState<ReferenceCrop>(FULL_REFERENCE_CROP);
  const [range, setRange] = useState<[number, number]>([0, 0]);
  const rangeRef = useRef(range); rangeRef.current = range;
  const [current, setCurrent] = useState(0), [playing, setPlaying] = useState(false), [muted, setMuted] = useState(false);
  const [frames, setFrames] = useState<string[]>([]), [frameError, setFrameError] = useState(false);
  const [mediaError, setMediaError] = useState(false), [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'uploading' | 'processing'>('idle');
  const [progress, setProgress] = useState(0);
  const busy = status !== 'idle', ready = metadata.duration >= MIN_REFERENCE_MS && metadata.duration <= 3600000 && !mediaError;
  const owner = useAuthStore(state => state.user?.id);
  const initialOwner = useRef(owner);
  useBodyScrollLock(true);
  useDialogFocus(dialog, true, () => { if (!busyRef.current) onClose(); });

  useEffect(() => { if (owner !== initialOwner.current) onClose(); }, [owner, onClose]);
  useEffect(() => {
    mounted.current = true;
    const src = source.file ? URL.createObjectURL(source.file) : getAssetStreamUrl(source.material.id);
    setUrl(src);
    return () => { mounted.current = false; if (source.file) URL.revokeObjectURL(src); };
  }, [source.file, source.material?.id]);
  useEffect(() => {
    const node = stage.current; if (!node) return;
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width - 32, height: entry.contentRect.height - 28 }));
    observer.observe(node); return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!url) return;
    const controller = new AbortController(); setFrames([]); setFrameError(false);
    void referenceVideoFrames(url, controller.signal, (data, index) => {
      if (!controller.signal.aborted) setFrames(previous => { const next = [...previous]; next[index] = data; return next; });
    }).catch(() => { if (!controller.signal.aborted) setFrameError(true); });
    return () => controller.abort();
  }, [url]);
  useEffect(() => {
    if (!playing) return;
    let raf: number;
    const tick = () => {
      const player = video.current;
      if (player && !player.seeking && pendingSeek.current === null) {
        const [start, end] = rangeRef.current;
        if (player.currentTime * 1000 >= end - 15 || player.currentTime * 1000 < start) seek(start);
        else setCurrent(player.currentTime * 1000);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf);
    // The loop reads the current element and selection through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, video]);

  function flushSeek() {
    if (seekFrame.current !== null) { cancelAnimationFrame(seekFrame.current); seekFrame.current = null; }
    const player = video.current, target = pendingSeek.current;
    if (!player || target === null || player.seeking) return;
    pendingSeek.current = null;
    // Do not restart decoding on every pointer event. seeked applies the latest
    // queued target; pointer-up also flushes so the final position is retained.
    if (Math.abs(player.currentTime * 1000 - target) > 1) player.currentTime = target / 1000;
  }
  function seek(ms: number) {
    if (!video.current || !metadata.duration) return;
    const value = clamp(ms, 0, Math.max(0, metadata.duration - 1));
    setCurrent(value); pendingSeek.current = value;
    if (seekFrame.current === null) seekFrame.current = requestAnimationFrame(() => { seekFrame.current = null; flushSeek(); });
  }
  function syncPosition() {
    if (pendingSeek.current !== null) { flushSeek(); return; }
    if (!gesture.current && video.current && !video.current.seeking) setCurrent(video.current.currentTime * 1000);
  }
  function pause() { if (video.current && !video.current.paused) video.current.pause(); setPlaying(false); }
  function updateRange(next: [number, number], preview: 'start' | 'end' = 'start') {
    rangeRef.current = next; setRange(next); pause(); seek(preview === 'end' ? next[1] - 1 : next[0]);
  }
  async function togglePlay() {
    const player = video.current;
    if (!player || !ready) return;
    if (!player.paused) { pause(); return; }
    const position = pendingSeek.current ?? player.currentTime * 1000;
    if (position < range[0] || position >= range[1] - 20) seek(range[0]);
    flushSeek();
    try { await player.play(); }
    catch { if (mounted.current) setError(t('previewFailed')); }
  }
  function begin(event: PointerEvent<HTMLElement>, kind: Gesture['kind'], handle: string) {
    if (!ready || busy || event.button !== 0 || event.isPrimary === false) return;
    event.stopPropagation(); event.preventDefault(); pause();
    const rect = (kind === 'crop' ? frame.current : track.current)?.getBoundingClientRect(); if (!rect?.width || !rect.height) return;
    gesture.current = { kind, handle, pointerId: event.pointerId, left: rect.left, x: event.clientX, y: event.clientY, width: rect.width, height: rect.height, crop, range: rangeRef.current };
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    if (kind === 'scrub') seek((event.clientX - rect.left) / rect.width * metadata.duration);
  }
  function move(event: PointerEvent<HTMLElement>) {
    const drag = gesture.current; if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.kind === 'scrub') { seek((event.clientX - drag.left) / drag.width * metadata.duration); return; }
    const dx = (event.clientX - drag.x) / drag.width, dy = (event.clientY - drag.y) / drag.height;
    if (drag.kind === 'crop') setCrop(resizeReferenceCrop(drag.crop, drag.handle, dx, dy, Math.min(1, 16 / metadata.width), Math.min(1, 16 / metadata.height)));
    else updateRange(trimReferenceRange(...drag.range, drag.handle as 'start' | 'end' | 'move', dx * metadata.duration, metadata.duration, maxDurationMs), drag.handle === 'end' ? 'end' : 'start');
  }
  function end(event: PointerEvent<HTMLElement>) {
    if (gesture.current?.pointerId !== event.pointerId) return;
    move(event); gesture.current = null; flushSeek();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }
  function cancelGesture(event: PointerEvent<HTMLElement>) {
    if (gesture.current?.pointerId !== event.pointerId) return;
    gesture.current = null; flushSeek();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }
  function scrubKey(event: KeyboardEvent) {
    if (!ready || busy || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault(); pause();
    const position = pendingSeek.current ?? video.current!.currentTime * 1000;
    seek(event.key === 'Home' ? 0 : event.key === 'End' ? metadata.duration - 1 : position + (event.key === 'ArrowLeft' ? -1 : 1) * (event.shiftKey ? 1000 : 100));
    flushSeek();
  }
  function cropKey(event: KeyboardEvent, handle: string) {
    if (!event.key.startsWith('Arrow') || busy) return;
    event.preventDefault(); const step = event.shiftKey ? 10 : 1;
    setCrop(resizeReferenceCrop(crop, handle, event.key === 'ArrowLeft' ? -step / metadata.width : event.key === 'ArrowRight' ? step / metadata.width : 0,
      event.key === 'ArrowUp' ? -step / metadata.height : event.key === 'ArrowDown' ? step / metadata.height : 0, 16 / metadata.width, 16 / metadata.height));
  }
  function timeKey(event: KeyboardEvent, handle: 'start' | 'end' | 'move') {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || busy) return;
    event.preventDefault(); const delta = event.key === 'Home' ? -metadata.duration : event.key === 'End' ? metadata.duration : (event.key === 'ArrowLeft' ? -1 : 1) * (event.shiftKey ? 1000 : 100);
    updateRange(trimReferenceRange(...rangeRef.current, handle, delta, metadata.duration, maxDurationMs), handle === 'end' ? 'end' : 'start');
    flushSeek();
  }
  async function confirm() {
    if (busyRef.current || !ready) return;
    busyRef.current = true; pause(); setError('');
    const session = getAuthSessionVersion();
    const valid = () => mounted.current && session === getAuthSessionVersion() && owner === useAuthStore.getState().user?.id;
    const controller = new AbortController(); editAbort.current = controller;
    try {
      if (!uploaded.current && source.file) {
        setStatus('uploading');
        const receipt = await referenceVideoEditApi.upload(source.file, value => { if (valid()) setProgress(value); }, controller.signal);
        if (!valid()) return;
        uploaded.current = receipt;
      }
      if (!uploaded.current) throw new Error('missing_source');
      setStatus('processing');
      const timeout = window.setTimeout(() => controller.abort(), 180000);
      let response;
      try {
        response = await referenceVideoEditApi.edit({ ...referenceEditPayload(uploaded.current.id, Math.round(range[0]), Math.round(range[1]), crop), upload: uploaded.current.upload }, controller.signal);
      } finally { window.clearTimeout(timeout); }
      if (!valid()) return;
      if (!response.success || !response.data?.url || response.data.file_type !== 'video') {
        const code = response.error?.code;
        // A failed PUT must be retried; a failed render retains the verified upload.
        if (source.file && (code === 'REFERENCE_VIDEO_UPLOAD_MISSING' || code === 'material_upload_content_mismatch')) uploaded.current = null;
        setError(t(code === 'REFERENCE_VIDEO_REUPLOAD_REQUIRED' ? 'reuploadRequired' : 'saveFailed')); return;
      }
      onConfirm(response.data);
    } catch { if (valid()) setError(t('saveFailed')); }
    finally { editAbort.current = null; busyRef.current = false; if (mounted.current) setStatus('idle'); }
  }
  const scale = Math.min(size.width / (metadata.width || 1), size.height / (metadata.height || 1));
  const selectedMs = range[1] - range[0];
  const percent = (ms: number) => metadata.duration ? ms / metadata.duration * 100 : 0;
  const pointer = { onPointerMove: move, onPointerUp: end, onPointerCancel: cancelGesture, onLostPointerCapture: cancelGesture };
  return createPortal(<div className={styles.overlay} onMouseDown={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t('title')} aria-busy={busy} className={`${creationChrome.chrome} ${styles.dialog}`}>
      <CreationGlassBackdrop />
      <header className={styles.header}><h2>{t('title')}</h2><button type="button" disabled={busy} onClick={onClose} aria-label={t('close')}><X size={20} /></button></header>
      <div ref={stage} className={styles.stage}>
        <div ref={frame} className={styles.frame} style={{ width: metadata.width ? metadata.width * scale : '100%', height: metadata.height ? metadata.height * scale : '100%' }}>
          <video ref={videoRef} src={url || undefined} muted={muted} playsInline preload="auto" crossOrigin="anonymous" onError={() => setMediaError(true)}
            onLoadedMetadata={event => { const player = event.currentTarget; const duration = Math.floor(player.duration * 1000); if (!Number.isFinite(duration) || !player.videoWidth) { setMediaError(true); return; } setMetadata({ width: player.videoWidth, height: player.videoHeight, duration }); setRange([0, Math.min(duration, maxDurationMs)]); }}
            onPlay={event => setPlaying(!event.currentTarget.paused)} onPlaying={event => setPlaying(!event.currentTarget.paused)} onPause={() => setPlaying(false)}
            onSeeked={syncPosition} onTimeUpdate={syncPosition}
            onEnded={() => { setPlaying(false); seek(rangeRef.current[0]); flushSeek(); }} />
          {ready && <div className={styles.cropMask} aria-hidden><span style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` }} /></div>}
          {ready && <div className={styles.crop} style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.width * 100}%`, height: `${crop.height * 100}%` }}>
            <button type="button" disabled={busy} className={styles.cropMove} aria-label={t('moveCrop')} onPointerDown={event => begin(event, 'crop', 'move')} onKeyDown={event => cropKey(event, 'move')} {...pointer}><span className={styles.grid} aria-hidden /></button>
            {handles.map(handle => <button key={handle} type="button" disabled={busy} className={`${styles.handle} ${styles[handle]}`} aria-label={t('resizeCrop', { position: t(`handles.${handle}`) })}
              onPointerDown={event => begin(event, 'crop', handle)} onKeyDown={event => cropKey(event, handle)} {...pointer}><span /></button>)}
          </div>}
        </div>
        {!metadata.duration && !mediaError && <div className={styles.loading} role="status"><SystemLoadingIcon className="h-6 w-6" /><span>{t('loading')}</span></div>}
        {(mediaError || metadata.duration > 0 && !ready) && <p className={styles.loading} role="alert">{t(mediaError ? 'loadFailed' : metadata.duration > 3600000 ? 'tooLong' : 'tooShort')}</p>}
      </div>
      <div className={styles.controls}>
        <button type="button" className={styles.play} disabled={!ready || busy} onClick={() => void togglePlay()} aria-label={t(playing ? 'pause' : 'play')}>{playing ? <Pause size={22} /> : <Play size={22} fill="currentColor" />}</button>
        <div className={styles.timeline}>
        <div ref={track} className={styles.track} data-reference-timeline role="slider" tabIndex={ready && !busy ? 0 : -1}
          aria-label={t('playhead')} aria-disabled={!ready || busy} aria-orientation="horizontal" aria-valuemin={0} aria-valuemax={metadata.duration}
          aria-valuenow={Math.round(current)} aria-valuetext={referenceTime(current)} onKeyDown={scrubKey}
          onPointerDown={event => begin(event, 'scrub', 'playhead')} {...pointer}>
          <div className={styles.frames} aria-hidden>{Array.from({ length: 14 }, (_, index) => <span key={index}>{frames[index] && <img src={frames[index]} alt="" draggable={false} />}</span>)}</div>
          <div className={styles.shade} style={{ left: 0, width: `${percent(range[0])}%` }} /><div className={styles.shade} style={{ right: 0, width: `${100 - percent(range[1])}%` }} />
          <span className={styles.playhead} data-reference-playhead style={{ left: `${percent(current)}%` }} aria-hidden />
        </div>
        {ready && <div className={styles.selection} style={{ left: `${percent(range[0])}%`, width: `${percent(selectedMs)}%` }}>
          <span className={styles.duration}>{referenceTime(selectedMs)}</span>
          <button type="button" disabled={busy || selectedMs >= metadata.duration} className={styles.moveRange} aria-label={t('moveSelection')} title={t('moveSelection')}
            onPointerDown={event => begin(event, 'timeline', 'move')} onKeyDown={event => timeKey(event, 'move')} {...pointer}>
            <GripHorizontal size={16} aria-hidden />
          </button>
          {(['start', 'end'] as const).map(handle => <button key={handle} type="button" role="slider" disabled={busy} aria-label={t(handle)} aria-orientation="horizontal" aria-valuemin={handle === 'start' ? Math.max(0, range[1] - maxDurationMs) : range[0] + MIN_REFERENCE_MS} aria-valuemax={handle === 'start' ? range[1] - MIN_REFERENCE_MS : Math.min(metadata.duration, range[0] + maxDurationMs)} aria-valuenow={Math.round(handle === 'start' ? range[0] : range[1])} aria-valuetext={referenceTime(handle === 'start' ? range[0] : range[1])}
            className={`${styles.trimHandle} ${styles[handle]}`} onPointerDown={event => begin(event, 'timeline', handle)} onKeyDown={event => timeKey(event, handle)} {...pointer}><span /></button>)}
        </div>}
        </div>
      </div>
      <div className={styles.meta}><span>{`${referenceTime(current)} / ${referenceTime(metadata.duration)}`}</span><span>{`${referenceTime(range[0])} – ${referenceTime(range[1])}`}</span><span>{t('rangeHint', { max: maxDurationMs / 1000 })}</span></div>
      <p className={styles.timelineHint}>{t('timelineHint')}</p>
      {frameError && !mediaError && <p className={styles.notice}>{t('framesUnavailable')}</p>}
      {error && <p role="alert" className={styles.error}>{error}</p>}
      <footer className={styles.footer}>
        <div><button type="button" disabled={!ready || busy} onClick={() => { setCrop(FULL_REFERENCE_CROP); updateRange([0, Math.min(metadata.duration, maxDurationMs)]); }}><RotateCcw size={16} /><span>{t('reset')}</span></button>
          <button type="button" disabled={!ready || busy} onClick={() => setMuted(value => !value)} aria-label={t(muted ? 'unmute' : 'mute')}>{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button></div>
        <StudioPrimaryAction type="button" className={`${creationChrome.action} ${styles.confirm}`} disabled={!ready || busy} onClick={() => void confirm()}>{busy && <SystemLoadingIcon className="h-4 w-4" />}<span>{t(status === 'uploading' ? 'uploading' : status === 'processing' ? 'processing' : 'confirm', { progress: Math.round(progress) })}</span></StudioPrimaryAction>
      </footer>
    </section>
  </div>, document.body);
}
